import { type ReactNode } from 'react';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import type { PropertyFileResponse, PropertyFileTreeNode, TrashedFileResponse } from '@/shared/api';
import { AkteBrowserMain } from './AkteBrowserMain';
import { AkteBrowserSidebar } from './AkteBrowserSidebar';
import { AkteFolderRail } from './AkteFolderRail';
import { AkteDetailHost } from './AkteDetailHost';
import { AkteDetailPane } from './AkteDetailPane';
import { AkteMultiSelectPane } from './AkteMultiSelectPane';
import type { FachansichtCallbacks } from './AkteFachansicht';
import { useAkteDragMove, type DragBulkConfig } from './useAkteDragMove';
import type { FileActions } from './akteFileActions';
import type { useAkteBrowser } from './useAkteBrowser';
import type { AkteBulkHandlers } from './useAkteBulkDialogs';

interface Props {
  propertyId: string;
  tree: PropertyFileTreeNode;
  trash: { data: TrashedFileResponse[] | undefined; isLoading: boolean };
  browser: ReturnType<typeof useAkteBrowser>;
  showTrash: boolean;
  onOpenTrash: () => void;
  onNavigate: (path: string) => void;
  actions: FileActions;
  selectedFiles: PropertyFileResponse[];
  bulk: AkteBulkHandlers;
  folderMenu: (node: PropertyFileTreeNode) => ReactNode;
  onUploadInto: (path: string) => void;
  /** Drag einer Datei auf den Sidebar-Papierkorb = Soft-Delete + Undo. */
  onTrashDrop: (file: PropertyFileResponse) => void;
  /** Drag einer Datei auf eine andere Kategorie = Reclassify — still oder Dialog. */
  onReclassifyDrop: (file: PropertyFileResponse, targetFolder: string) => void;
  /** Bulk-Drag: eine Mehrfach-Auswahl als Payload ziehen → Bulk-Move/Reclassify/Trash. */
  bulkDrag: DragBulkConfig;
  fachansicht: FachansichtCallbacks;
}

/** Drei-Zonen-Layout: Sidebar-Baum · Content (Ordner/Papierkorb) · Detail-Pane. */
export function AkteBrowserLayout({
  propertyId,
  tree,
  trash,
  browser,
  showTrash,
  onOpenTrash,
  onNavigate,
  actions,
  selectedFiles,
  bulk,
  folderMenu,
  onUploadInto,
  onTrashDrop,
  onReclassifyDrop,
  bulkDrag,
  fachansicht,
}: Props) {
  // Eine gemeinsame Drag-Instanz, damit die Content-Zone (Quelle) UND die Sidebar (Drop-Ziel)
  // denselben Zustand teilen — sonst ist eine Datei in einen Ordner, der aktuell keine
  // Unterordner in der Content-Zone zeigt, nicht per Drag erreichbar.
  const drag = useAkteDragMove(actions.onMoveTo, onTrashDrop, onReclassifyDrop, bulkDrag);

  // `lg` ist der Breakpoint, ab dem das Layout dreispaltig wird — darunter ist die Pane ein Sheet.
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const single = selectedFiles.length === 1 ? selectedFiles[0]! : null;
  // Mobil legt sich das Sheet über die Liste. Beim Sammeln (Checkbox, Ctrl, „alle") bliebe die
  // nächste Datei damit unerreichbar — dort bleibt die Pane dem Desktop vorbehalten.
  const showPane =
    !showTrash &&
    selectedFiles.length > 0 &&
    (isDesktop || (browser.selection.intent === 'pick' && single !== null));

  return (
    <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-start">
      <AkteFolderRail currentKey={showTrash ? '#papierkorb' : browser.folderPath}>
        <AkteBrowserSidebar
          tree={tree}
          currentPath={browser.folderPath}
          trashCount={trash.data?.length ?? 0}
          trashActive={showTrash}
          onNavigate={onNavigate}
          onOpenTrash={onOpenTrash}
          renderMenu={folderMenu}
          drop={drag.dropProps}
          trashDrop={drag.trashDropProps()}
        />
      </AkteFolderRail>

      <main className="min-w-0 flex-1">
        <AkteBrowserMain
          propertyId={propertyId}
          tree={tree}
          trash={trash}
          browser={browser}
          showTrash={showTrash}
          actions={actions}
          bulk={bulk}
          folderMenu={folderMenu}
          onUploadInto={onUploadInto}
          drag={drag}
          fachansicht={fachansicht}
        />
      </main>

      {showPane && (
        <AkteDetailHost
          desktop={isDesktop}
          title={
            single ? `Details zu ${single.fileName}` : `${selectedFiles.length} Dateien ausgewählt`
          }
          description={
            single
              ? 'Vorschau, Metadaten und Aktionen der gewählten Datei'
              : 'Die aktuelle Mehrfachauswahl und ihr Umfang'
          }
          onClose={browser.selection.clear}
        >
          {single ? (
            <AkteDetailPane
              propertyId={propertyId}
              file={single}
              actions={actions}
              onClose={browser.selection.clear}
            />
          ) : (
            <AkteMultiSelectPane
              files={selectedFiles}
              onDeselect={browser.selection.toggle}
              onClear={browser.selection.clear}
            />
          )}
        </AkteDetailHost>
      )}
    </div>
  );
}
